/**
 * TipTap extension for @user mentions.
 *
 * Trigger: @ character
 * Renders as: <span data-type="userMention" data-id="userId" data-label="Name">Name</span>
 * Serializes to markdown as: [Name](user:userId)
 */
import Mention from "@tiptap/extension-mention";
import { mergeAttributes } from "@tiptap/core";
import { PluginKey } from "@tiptap/pm/state";
import Suggestion from "@tiptap/suggestion";
import { createMentionSerializeSpec, createMentionParseSpec } from "./mention-markdown";
import { createSuggestionRenderer, SuggestionItem } from "./suggestion-renderer";
import { MentionType } from "./mention-type.enum";
import MentionSuggestionList from "./MentionSuggestionList.vue";

export interface UserMentionOptions {
  /** Function to fetch user suggestions for a query string */
  fetchSuggestions: (query: string) => Promise<SuggestionItem[]> | SuggestionItem[];
  HTMLAttributes: Record<string, any>;
}

export const UserMention = Mention.extend<UserMentionOptions>({
  name: MentionType.USER,

  addOptions() {
    return {
      fetchSuggestions: () => [],
      HTMLAttributes: {
        class: "mention mention--user"
      },
      suggestion: {},
      suggestions: [],
      renderText({ node }: any) {
        return `@${node.attrs.label ?? node.attrs.id}`;
      },
      renderHTML({ node, options }: any) {
        return [
          "span",
          mergeAttributes({ "data-type": MentionType.USER }, options.HTMLAttributes),
          `@${node.attrs.label ?? node.attrs.id}`
        ];
      },
      deleteTriggerWithBackspace: false
    };
  },

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute("data-id"),
        renderHTML: (attributes: any) => {
          if (!attributes.id) return {};
          return { "data-id": attributes.id };
        }
      },
      label: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute("data-label"),
        renderHTML: (attributes: any) => {
          if (!attributes.label) return {};
          return { "data-label": attributes.label };
        }
      }
    };
  },

  parseHTML() {
    return [{ tag: `span[data-type="${MentionType.USER}"]` }];
  },

  renderHTML({ node, HTMLAttributes }: any) {
    return [
      "span",
      mergeAttributes({ "data-type": MentionType.USER }, this.options.HTMLAttributes, HTMLAttributes),
      `@${node.attrs.label ?? node.attrs.id}`
    ];
  },

  addStorage() {
    return {
      markdown: {
        serialize: createMentionSerializeSpec("@").serialize,
        parse: createMentionParseSpec()
      }
    };
  },

  addProseMirrorPlugins() {
    const fetchSuggestions = this.options.fetchSuggestions;

    const suggestion = {
      char: "@",
      items: ({ query }: { query: string }) => fetchSuggestions(query),
      render: createSuggestionRenderer(MentionSuggestionList),
      command: ({ editor, range, props }: any) => {
        const nodeAfter = editor.view.state.selection.$to.nodeAfter;
        const overrideSpace = nodeAfter?.text?.startsWith(" ");
        if (overrideSpace) {
          range.to += 1;
        }
        editor
          .chain()
          .focus()
          .insertContentAt(range, [
            { type: this.name, attrs: props },
            { type: "text", text: " " }
          ])
          .run();
        editor.view.dom.ownerDocument.defaultView?.getSelection()?.collapseToEnd();
      },
      allow: ({ state, range }: any) => {
        const $from = state.doc.resolve(range.from);
        const type = state.schema.nodes[this.name];
        return !!$from.parent.type.contentMatch.matchType(type);
      }
    };

    return [
      Suggestion({
        ...suggestion,
        editor: this.editor,
        pluginKey: new PluginKey(MentionType.USER)
      })
    ];
  }
});
